import { useCallback, useState } from 'react';

import useFetch from 'hooks/useFetch';

import { AIRSPACE_API_HOST, DEFAULT_RADIUS_NM, JSON_HEADERS, WEATHER_PATH } from './constants';
import type {
    AirspaceWeather,
    DroneWeather,
    FetchAirspaceWeather,
    FlightCategory,
    ForecastEntry,
    UseAirspaceWeatherReturn,
    VfrAdvisory
} from './types';

const FLIGHT_CATEGORIES: readonly FlightCategory[] = [
    'VFR',
    'MVFR',
    'IFR',
    'LIFR',
    'UNKNOWN'
] as const;

const isString = (value: unknown): value is string => typeof value === 'string';

const isNumber = (value: unknown): value is number =>
    typeof value === 'number' && !Number.isNaN(value);

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

// Aviation Weather sends visibility as either a number (standard METAR) or a
// string like "10+", "6SM", "P6SM". Parse a leading number out of the string
// form and drop anything that doesn't start with a digit.
const normalizeVisibility = (raw: unknown): number | null => {
    if (isNumber(raw)) {
        return raw;
    }

    if (!isString(raw)) {
        return null;
    }

    const parsed = Number.parseFloat(raw);

    return Number.isFinite(parsed) ? parsed : null;
};

const normalizeFlightCategory = (raw: unknown): FlightCategory => {
    if (isString(raw) && (FLIGHT_CATEGORIES as readonly string[]).includes(raw)) {
        return raw as FlightCategory;
    }

    return 'UNKNOWN';
};

const normalizeVfrAdvisory = (raw: unknown): VfrAdvisory => {
    const record = asRecord(raw);

    return {
        ceilingOk: record.ceiling_ok === true,
        disclaimer: isString(record.disclaimer) ? record.disclaimer : '',
        visibilityOk: record.visibility_ok === true,
        windOk: record.wind_ok === true
    };
};

const normalizeDroneWeather = (raw: unknown): DroneWeather | null => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const record = asRecord(raw);

    return {
        altimeterHpa: isNumber(record.altimeter_hpa) ? record.altimeter_hpa : null,
        ceilingFt: isNumber(record.ceiling_ft) ? record.ceiling_ft : null,
        dewpointC: isNumber(record.dewpoint_c) ? record.dewpoint_c : null,
        flightCategory: normalizeFlightCategory(record.flight_category),
        observationTime: isString(record.observation_time) ? record.observation_time : null,
        rawMetar: isString(record.raw_metar) ? record.raw_metar : '',
        station: isString(record.station) ? record.station : 'Unknown',
        temperatureC: isNumber(record.temperature_c) ? record.temperature_c : null,
        vfrAdvisory: normalizeVfrAdvisory(record.vfr_advisory),
        visibilitySm: normalizeVisibility(record.visibility_sm),
        windDirection: isNumber(record.wind_direction)
            ? record.wind_direction
            : isString(record.wind_direction)
              ? record.wind_direction
              : null,
        windGustKt: isNumber(record.wind_gust_kt) ? record.wind_gust_kt : null,
        windSpeedKt: isNumber(record.wind_speed_kt) ? record.wind_speed_kt : null
    };
};

const normalizeForecast = (raw: unknown): ForecastEntry[] => {
    if (!Array.isArray(raw)) {
        return [];
    }

    return raw
        .map(item => {
            const record = asRecord(item);

            return {
                icao: isString(record.icao) ? record.icao : '',
                issued: isString(record.issued) ? record.issued : '',
                raw: isString(record.raw) ? record.raw : ''
            };
        })
        .filter(entry => entry.raw.length > 0);
};

const normalizeWeather = (raw: unknown): AirspaceWeather | null => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const record = asRecord(raw);

    return {
        droneWeather: normalizeDroneWeather(record.drone_weather),
        flightCategory: normalizeFlightCategory(record.flight_category),
        forecast: normalizeForecast(record.forecast)
    };
};

const useAirspaceWeather = (): UseAirspaceWeatherReturn => {
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<AirspaceWeather | null>(null);
    const { authenticatedGet } = useFetch({ apiHost: AIRSPACE_API_HOST, headers: JSON_HEADERS });

    const fetchWeather: FetchAirspaceWeather = useCallback(
        async ({ lat, lng }) => {
            try {
                setIsLoading(true);

                const url = `${WEATHER_PATH}?lat=${lat}&lng=${lng}&radius_nm=${DEFAULT_RADIUS_NM}`;
                const response = await authenticatedGet<unknown>(url);

                if (!response || response.error || !response.data) {
                    setData(null);

                    return null;
                }

                const normalized = normalizeWeather(response.data);

                setData(normalized);

                return normalized;
            } catch (error) {
                // Weather is supplementary context, not safety-critical. Log and
                // fall back to a null display rather than surfacing an error toast.
                console.warn('Airspace weather fetch failed', error);
                setData(null);

                return null;
            } finally {
                setIsLoading(false);
            }
        },
        [authenticatedGet]
    );

    return { data, fetchWeather, isLoading };
};

export default useAirspaceWeather;
